import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Chip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { formatDate } from '../../utils/dateUtils';
import historiaService from '../../services/historiaService';

const EstadisticasEtapas = ({ historiaClinicaId, programaId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchEstadisticas = async () => {
      if (!historiaClinicaId || !programaId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Obtener estadísticas de la historia clínica
        const stats = await historiaService.getEstadisticasEtapas(historiaClinicaId);
        setEstadisticas(stats);
      } catch (err) {
        console.error("Error al cargar estadísticas:", err);
        setError("No se pudieron cargar las estadísticas");
      } finally {
        setLoading(false);
      }
    };

    fetchEstadisticas();
  }, [historiaClinicaId, programaId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={30} />
        <Typography variant="body2" ml={2}>Cargando estadísticas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2} bgcolor="#FFF3F3" borderRadius={1}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!estadisticas) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          No hay datos estadísticos disponibles
        </Typography>
      </Box>
    );
  }

  // Datos para gráfico de sesiones por etapa
  const sesionesPorEtapa = estadisticas.etapas.map(etapa => ({
    name: etapa.nombre,
    Realizadas: etapa.sesiones_realizadas,
    Programadas: etapa.sesiones_programadas,
    Canceladas: etapa.sesiones_canceladas
  }));

  // Datos para gráfico de progreso general
  const progresoGeneral = [
    { name: 'Completado', value: estadisticas.porcentaje_completado },
    { name: 'Pendiente', value: 100 - estadisticas.porcentaje_completado }
  ];

  // Colores para gráficos
  const COLORS = ['#4caf50', '#2196f3', '#f44336', '#ff9800'];
  const PROGRESO_COLORS = ['#4caf50', '#e0e0e0'];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Estadísticas del Tratamiento
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Resumen" />
          <Tab label="Por Etapas" />
          <Tab label="Detalles" />
        </Tabs>

        {/* Pestaña de Resumen */}
        {tabValue === 0 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box p={2} textAlign="center">
                  <Typography variant="subtitle1" gutterBottom>
                    Progreso General
                  </Typography>
                  <Box position="relative" display="inline-flex" p={2}>
                    <CircularProgress
                      variant="determinate"
                      value={estadisticas.porcentaje_completado}
                      size={120}
                      thickness={5}
                      sx={{ color: '#4caf50' }}
                    />
                    <Box
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                    >
                      <Typography
                        variant="h4"
                        component="div"
                        color="text.secondary"
                      >
                        {`${Math.round(estadisticas.porcentaje_completado)}%`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <Chip 
                      label={`${estadisticas.etapas_completadas} de ${estadisticas.total_etapas} etapas`} 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box p={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Sesiones
                  </Typography>
                  <Box mt={2}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Total:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="medium">
                          {estadisticas.total_sesiones}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Realizadas:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          {estadisticas.sesiones_realizadas}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Programadas:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="medium" color="primary.main">
                          {estadisticas.sesiones_programadas}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Canceladas:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="medium" color="error.main">
                          {estadisticas.sesiones_canceladas}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box p={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Etapa Actual:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="primary.main">
                    {estadisticas.etapa_actual?.nombre || 'No definida'}
                  </Typography>
                  {estadisticas.etapa_actual && (
                    <Box mt={1}>
                      <LinearProgress
                        variant="determinate"
                        value={estadisticas.etapa_actual.porcentaje_completado}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {Math.round(estadisticas.etapa_actual.porcentaje_completado)}% completado
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box p={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fechas:
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Inicio:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {estadisticas.fecha_inicio ? formatDate(estadisticas.fecha_inicio) : 'No iniciado'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Última sesión:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {estadisticas.fecha_ultima_sesion ? formatDate(estadisticas.fecha_ultima_sesion) : 'Sin sesiones'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Próxima sesión:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={estadisticas.fecha_proxima_sesion ? 'primary.main' : 'textSecondary'}>
                        {estadisticas.fecha_proxima_sesion ? formatDate(estadisticas.fecha_proxima_sesion) : 'No programada'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Pestaña de Etapas */}
        {tabValue === 1 && (
          <Box height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sesionesPorEtapa}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="Realizadas" stackId="a" fill="#4caf50" />
                <Bar dataKey="Programadas" stackId="a" fill="#2196f3" />
                <Bar dataKey="Canceladas" stackId="a" fill="#f44336" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Pestaña de Detalles */}
        {tabValue === 2 && (
          <Box>
            {estadisticas.etapas.map((etapa, index) => (
              <Box key={index} mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {etapa.nombre}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box width="100%" mr={1}>
                    <LinearProgress
                      variant="determinate"
                      value={etapa.porcentaje_completado}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box minWidth={35}>
                    <Typography variant="body2" color="textSecondary">
                      {`${Math.round(etapa.porcentaje_completado)}%`}
                    </Typography>
                  </Box>
                </Box>
                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                  <Chip 
                    size="small" 
                    label={`${etapa.sesiones_realizadas} realizadas`} 
                    color="success" 
                    variant="outlined" 
                  />
                  <Chip 
                    size="small" 
                    label={`${etapa.sesiones_programadas} programadas`} 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    size="small" 
                    label={`${etapa.sesiones_canceladas} canceladas`} 
                    color="error" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EstadisticasEtapas;